import { POI_CATEGORY_COLORS, POI_DOT_COLOR_BY_FILL } from "components/map/markerVisualTokens";
import type { LonLat } from "utils/curveLine";

/**
 * What every partner map draws the same way: the colours of a nearby place by its group, and the frame of a set of
 * points. The zoom ladders and the markers of one partner's map stay with that partner.
 */

export type PoiGroup = "do" | "food" | "out";

/** The part of a nearby place the shared marker draws. */
export interface PoiMarkerShape {
	id: string;
	name: string;
	grp: PoiGroup;
	/** a key of markerIcons.json */
	icon: string;
	photo?: string;
	active: boolean;
	/** a wordmark drawn on a pill in place of the drawing (a sister restaurant): the pill keeps its name plate at every zoom, reading `plate` in place of the name */
	logo?: string;
	plate?: string;
	/** of a logo marker: the colour of the name plate's text (white when unset), the rim of the round marker, and `round`, a circle filled with the marker's colour in place of the white pill */
	ink?: string;
	ring?: string;
	round?: true;
}

// The app's own marker colours (markerVisualTokens): a nearby place like the app's things to do, food and drink,
// outdoors. A dot is the saturated version of the tint.
export const POI_FILL: Record<PoiGroup, string> = {
	do: POI_CATEGORY_COLORS["things-to-do"].fill,
	food: POI_CATEGORY_COLORS["food-drink"].fill,
	out: POI_CATEGORY_COLORS.outdoors.fill,
};

export const POI_DOT: Record<PoiGroup, string> = {
	do: POI_DOT_COLOR_BY_FILL[POI_FILL.do],
	food: POI_DOT_COLOR_BY_FILL[POI_FILL.food],
	out: POI_DOT_COLOR_BY_FILL[POI_FILL.out],
};

/** South-west and north-east corners of the points, or null for none. One point gets no extent: the camera zooms in on it. */
export function boundsOf(points: LonLat[]): [LonLat, LonLat] | null {
	if (!points.length) return null;
	const lons = points.map((p) => p[0]);
	const lats = points.map((p) => p[1]);
	return [
		[Math.min(...lons), Math.min(...lats)],
		[Math.max(...lons), Math.max(...lats)],
	];
}
